

import React, { useEffect, useMemo, useRef, useState } from "react";
import Navbar from "../components/navbar";
import "../css/trivia.css";
import { getApp } from "firebase/app";
import { doc, getDoc, getFirestore, serverTimestamp, setDoc } from "firebase/firestore";
/**
 * EditTrivia
 * A simple builder page for a trivia wheel:
 * - Add categories
 * - Add questions/answers assigned to categories
 * - Import from CSV
 *
 * CSV format (header row required):
 * category,question,answer
 */

function uid(prefix = "id") {
    return `${prefix}_${Math.random().toString(16).slice(2)}_${Date.now()}`;
}

// Lightweight CSV parser that supports quoted fields and commas inside quotes.
function parseCsv(text) {
    const rows = [];
    let row = [];
    let field = "";
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        const next = text[i + 1];

        if (c === '"') {
            if (inQuotes && next === '"') {
                // Escaped quote
                field += '"';
                i++;
            } else {
                inQuotes = !inQuotes;
            }
            continue;
        }

        if (!inQuotes && (c === ",")) {
            row.push(field);
            field = "";
            continue;
        }

        if (!inQuotes && (c === "\n")) {
            row.push(field);
            rows.push(row);
            row = [];
            field = "";
            continue;
        }

        if (!inQuotes && c === "\r") {
            // ignore CR; handle CRLF via \n case
            continue;
        }

        field += c;
    }

    // last field
    row.push(field);
    // avoid pushing trailing empty line
    const isTrailingEmpty = row.length === 1 && row[0].trim() === "";
    if (!isTrailingEmpty) rows.push(row);

    return rows;
}

export default function EditTrivia() {
    const [categories, setCategories] = useState([]); // {id, name}
    const [questions, setQuestions] = useState([]); // {id, categoryId, question, answer}

    const [newCategoryName, setNewCategoryName] = useState("");

    const [qCategoryId, setQCategoryId] = useState("");
    const [qQuestion, setQQuestion] = useState("");
    const [qAnswer, setQAnswer] = useState("");
    const [questionsTabCategoryId, setQuestionsTabCategoryId] = useState("all"); // "all" or categoryId

    const [importError, setImportError] = useState("");
    const [importInfo, setImportInfo] = useState("");
    const fileInputRef = useRef(null);

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState("");
    const [saveInfo, setSaveInfo] = useState("");

    const hasLoadedRef = useRef(false);
    const saveTimerRef = useRef(null);

    // For now we store a single editable trivia set. Later we can parameterize by route or id.
    const TRIVIA_DOC_PATH = "triviaSets/default";

    function getDbSafe() {
        // Assumes Firebase has been initialized somewhere in the app.
        // If it hasn't, this will throw and we surface a readable error.
        return getFirestore(getApp());
    }

    const categoryById = useMemo(() => {
        const m = new Map();
        for (const c of categories) m.set(c.id, c);
        return m;
    }, [categories]);

    function addCategory() {
        const name = newCategoryName.trim();
        if (!name) return;

        // de-dupe by name (case-insensitive)
        const exists = categories.some((c) => c.name.toLowerCase() === name.toLowerCase());
        if (exists) {
            setNewCategoryName("");
            return;
        }

        const id = uid("cat");
        setCategories((prev) => [...prev, { id, name }]);
        setNewCategoryName("");

        // If no selection yet, default to the first created category.
        setQCategoryId((prev) => prev || id);
    }

    function removeCategory(categoryId) {
        setCategories((prev) => prev.filter((c) => c.id !== categoryId));
        setQuestions((prev) => prev.filter((q) => q.categoryId !== categoryId));

        setQCategoryId((prev) => {
            if (prev !== categoryId) return prev;
            const remaining = categories.filter((c) => c.id !== categoryId);
            return remaining[0]?.id || "";
        });
    }

    function addQuestion() {
        const catId = qCategoryId;
        const questionText = qQuestion.trim();
        const answerText = qAnswer.trim();

        if (!catId || !questionText || !answerText) return;

        setQuestions((prev) => [
            ...prev,
            {
                id: uid("q"),
                categoryId: catId,
                question: questionText,
                answer: answerText,
            },
        ]);

        setQQuestion("");
        setQAnswer("");
    }

    function removeQuestion(questionId) {
        setQuestions((prev) => prev.filter((q) => q.id !== questionId));
    }

    function updateCategoryName(categoryId, nextNameRaw) {
        const nextName = (nextNameRaw ?? "").toString();
        setCategories((prev) => prev.map((c) => (c.id === categoryId ? { ...c, name: nextName } : c)));
    }

    function updateQuestion(questionId, patch) {
        setQuestions((prev) => prev.map((q) => (q.id === questionId ? { ...q, ...patch } : q)));
    }

    function downloadTemplate() {
        const csv = "category,question,answer\nHistory,Who was the first US president?,George Washington\nScience,What is H2O commonly called?,Water\n";
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "trivia_template.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }

    async function loadFromFirestore() {
        setSaveError("");
        setSaveInfo("");
        setIsLoading(true);

        try {
            const db = getDbSafe();
            const ref = doc(db, TRIVIA_DOC_PATH);
            const snap = await getDoc(ref);

            if (snap.exists()) {
                const data = snap.data() || {};
                const loadedCategories = Array.isArray(data.categories) ? data.categories : [];
                const loadedQuestions = Array.isArray(data.questions) ? data.questions : [];

                setCategories(loadedCategories);
                setQuestions(loadedQuestions);

                // default selection
                const firstCatId = loadedCategories[0]?.id || "";
                setQCategoryId((prev) => prev || firstCatId);

                setSaveInfo("Loaded from Firebase.");
            } else {
                setSaveInfo("No saved trivia found yet. Start adding categories/questions.");
            }

            hasLoadedRef.current = true;
        } catch (err) {
            console.error(err);
            setSaveError("Could not load trivia from Firebase. Make sure Firebase is initialized and Firestore rules allow reads.");
        } finally {
            setIsLoading(false);
        }
    }

    async function saveToFirestore(reason = "") {
        setSaveError("");
        setSaveInfo("");
        setIsSaving(true);

        try {
            const db = getDbSafe();
            const ref = doc(db, TRIVIA_DOC_PATH);

            // Ensure we only store plain JSON-ish data
            const payload = {
                categories: categories.map((c) => ({ id: c.id, name: c.name })),
                questions: questions.map((q) => ({
                    id: q.id,
                    categoryId: q.categoryId,
                    question: q.question,
                    answer: q.answer,
                })),
                updatedAt: serverTimestamp(),
            };

            await setDoc(ref, payload, { merge: true });
            setSaveInfo(reason ? `Saved (${reason}).` : "Saved.");
        } catch (err) {
            console.error(err);
            setSaveError("Could not save to Firebase. Check Firestore rules and your network connection.");
        } finally {
            setIsSaving(false);
        }
    }

    function mergeImportedData(items) {
        // items: {category, question, answer}[]
        // 1) Ensure categories exist; map category name -> id
        const nameToId = new Map(categories.map((c) => [c.name.toLowerCase(), c.id]));
        const newCategories = [];

        for (const it of items) {
            const name = (it.category || "").trim();
            if (!name) continue;
            const key = name.toLowerCase();
            if (!nameToId.has(key)) {
                const id = uid("cat");
                nameToId.set(key, id);
                newCategories.push({ id, name });
            }
        }

        // 2) Add questions
        const newQuestions = [];
        for (const it of items) {
            const catName = (it.category || "").trim();
            const q = (it.question || "").trim();
            const a = (it.answer || "").trim();
            if (!catName || !q || !a) continue;
            const catId = nameToId.get(catName.toLowerCase());
            if (!catId) continue;
            newQuestions.push({ id: uid("q"), categoryId: catId, question: q, answer: a });
        }

        setCategories((prev) => [...prev, ...newCategories]);
        setQuestions((prev) => [...prev, ...newQuestions]);

        if (!qCategoryId) {
            const first = (categories[0] || newCategories[0])?.id;
            if (first) setQCategoryId(first);
        }

        return { addedCategories: newCategories.length, addedQuestions: newQuestions.length };
    }

    async function handleCsvFile(file) {
        setImportError("");
        setImportInfo("");

        if (!file) return;

        const text = await file.text();
        const rows = parseCsv(text);
        if (!rows.length) {
            setImportError("CSV looks empty.");
            return;
        }

        const header = rows[0].map((h) => (h || "").trim().toLowerCase());
        const categoryIdx = header.indexOf("category");
        const questionIdx = header.indexOf("question");
        const answerIdx = header.indexOf("answer");

        if (categoryIdx === -1 || questionIdx === -1 || answerIdx === -1) {
            setImportError("CSV must have a header row with columns: category, question, answer");
            return;
        }

        const items = [];
        for (let i = 1; i < rows.length; i++) {
            const r = rows[i];
            const category = r[categoryIdx] ?? "";
            const question = r[questionIdx] ?? "";
            const answer = r[answerIdx] ?? "";
            // skip fully blank rows
            if (`${category}${question}${answer}`.trim() === "") continue;
            items.push({ category, question, answer });
        }

        if (!items.length) {
            setImportError("No data rows found (after the header).");
            return;
        }

        const { addedCategories, addedQuestions } = mergeImportedData(items);
        setImportInfo(`Imported: ${addedCategories} new categories, ${addedQuestions} questions.`);

        // reset file input so you can re-import same file if needed
        if (fileInputRef.current) fileInputRef.current.value = "";
    }

    useEffect(() => {
        loadFromFirestore();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!hasLoadedRef.current) return;

        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => {
            saveToFirestore("autosave");
        }, 800);

        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categories, questions]);

    useEffect(() => {
        if (questionsTabCategoryId === "all") return;

        const stillExists = categories.some((c) => c.id === questionsTabCategoryId);
        if (!stillExists) setQuestionsTabCategoryId("all");
    }, [categories, questionsTabCategoryId]);

    const totalByCategory = useMemo(() => {
        const counts = new Map();
        for (const c of categories) counts.set(c.id, 0);
        for (const q of questions) counts.set(q.categoryId, (counts.get(q.categoryId) || 0) + 1);
        return counts;
    }, [categories, questions]);

    const canAddQuestion = categories.length > 0;
    const filteredQuestions = useMemo(() => {
        if (questionsTabCategoryId === "all") return questions;
        return questions.filter((q) => q.categoryId === questionsTabCategoryId);
    }, [questions, questionsTabCategoryId]);

    return (
        <>
            <Navbar />
            <div className="triviaEditor">

                <h1 style={{ marginTop: 0 }}>Trivia Wheel Builder</h1>
                <p className="subtle">
                    Add categories and questions now. Next step: the actual wheel + play mode.
                </p>

                <div className="triviaPanel" style={{ marginTop: 14 }}>
                    <div className="triviaPanelHeader">
                        <div>
                            <h2 style={{ margin: 0, fontSize: 18 }}>Save / Load</h2>
                            <div className="triviaHint">
                                {isLoading ? "Loading…" : isSaving ? "Saving…" : "Changes autosave."}
                            </div>
                        </div>
                        <div className="triviaRow">
                            <button
                                type="button"
                                className="triviaBtn"
                                onClick={() => loadFromFirestore()}
                                disabled={isLoading || isSaving}
                            >
                                Reload
                            </button>
                            <button
                                type="button"
                                className="triviaBtn"
                                onClick={() => saveToFirestore("manual")}
                                disabled={isLoading || isSaving}
                            >
                                Save now
                            </button>
                        </div>
                    </div>
                    {saveError ? <div className="triviaError">{saveError}</div> : null}
                    {saveInfo ? <div className="triviaInfo">{saveInfo}</div> : null}
                </div>

                {/* Import */}
                <div className="triviaPanel">
                    <div className="triviaPanelHeader">
                        <div>
                            <h2 style={{ margin: 0, fontSize: 18 }}>Import</h2>
                            <div className="triviaHint">
                                CSV must have columns: <b>category</b>, <b>question</b>, <b>answer</b>
                            </div>
                        </div>
                        <div className="triviaRow">
                            <button type="button" className="triviaBtn" onClick={downloadTemplate}>
                                Download template
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,text/csv"
                                className="triviaFile"
                                onChange={(e) => handleCsvFile(e.target.files?.[0])}
                            />
                        </div>
                    </div>

                    {importError ? (
                        <div className="triviaError">{importError}</div>
                    ) : null}
                    {importInfo ? (
                        <div className="triviaInfo">{importInfo}</div>
                    ) : null}
                </div>

                <div className="triviaGrid2">
                    {/* Categories */}
                    <div className="triviaPanel">
                        <h2 style={{ marginTop: 0, fontSize: 18 }}>Categories</h2>

                        <div className="triviaRow">
                            <input
                                type="text"
                                placeholder="Add category (e.g., Church History)"
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") addCategory();
                                }}
                                className="triviaInput"
                            />
                            <button type="button" className="triviaBtn" onClick={addCategory}>
                                Add
                            </button>
                        </div>

                        <div>
                            {categories.length === 0 ? (
                                <div className="triviaMuted">No categories yet. Add one above or import a CSV.</div>
                            ) : (
                                <div className="triviaList">
                                    {categories.map((c) => (
                                        <div key={c.id} className="triviaItem">
                                            <div>
                                                <input
                                                    className="triviaInlineInput triviaCategoryNameInput"
                                                    value={c.name}
                                                    onChange={(e) => updateCategoryName(c.id, e.target.value)}
                                                    placeholder="Category name"
                                                />
                                                <div className="triviaItemMeta">
                                                    {totalByCategory.get(c.id) || 0} question(s)
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="triviaBtn triviaBtnDanger"
                                                onClick={() => removeCategory(c.id)}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Add Question */}
                    <div className="triviaPanel">
                        <h2 style={{ marginTop: 0, fontSize: 18 }}>Add question</h2>

                        {!canAddQuestion ? (
                            <div className="triviaMuted">Add at least 1 category first.</div>
                        ) : (
                            <>
                                <div className="triviaRow">
                                    <label className="triviaField">
                                        <span className="triviaLabel">Category</span>
                                        <select
                                            className="triviaSelect"
                                            value={qCategoryId}
                                            onChange={(e) => setQCategoryId(e.target.value)}
                                        >
                                            <option value="" disabled>
                                                Select a category
                                            </option>
                                            {categories.map((c) => (
                                                <option key={c.id} value={c.id}>
                                                    {c.name}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>

                                <div style={{ marginTop: 10 }}>
                                    <label className="triviaField">
                                        <span className="triviaLabel">Question</span>
                                        <textarea
                                            className="triviaTextarea"
                                            rows={3}
                                            value={qQuestion}
                                            onChange={(e) => setQQuestion(e.target.value)}
                                            placeholder="Type the question..."
                                        />
                                    </label>

                                    <label className="triviaField">
                                        <span className="triviaLabel">Answer</span>
                                        <textarea
                                            className="triviaTextarea"
                                            rows={3}
                                            value={qAnswer}
                                            onChange={(e) => setQAnswer(e.target.value)}
                                            placeholder="Type the answer..."
                                        />
                                    </label>

                                    <div className="triviaRow">
                                        <button
                                            type="button"
                                            className="triviaBtn"
                                            onClick={addQuestion}
                                            disabled={!qCategoryId || !qQuestion.trim() || !qAnswer.trim()}
                                        >
                                            Add question
                                        </button>
                                        <div style={{ opacity: 0.7, fontSize: 12 }}>
                                            Total: <b>{questions.length}</b>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Questions list */}
                <div className="triviaPanel">
                    <div className="triviaPanelHeader">
                        <h2 style={{ margin: 0, fontSize: 18 }}>Questions</h2>
                        <div className="triviaHint">
                            Tip: You can import more CSV later; it will merge categories by name.
                        </div>
                    </div>
                    <div className="triviaTabs" role="tablist" aria-label="Question categories">
                        <button
                            type="button"
                            className={questionsTabCategoryId === "all" ? "triviaTab triviaTabActive" : "triviaTab"}
                            onClick={() => setQuestionsTabCategoryId("all")}
                            role="tab"
                            aria-selected={questionsTabCategoryId === "all"}
                        >
                            All
                        </button>

                        {categories.map((c) => (
                            <button
                                key={c.id}
                                type="button"
                                className={questionsTabCategoryId === c.id ? "triviaTab triviaTabActive" : "triviaTab"}
                                onClick={() => setQuestionsTabCategoryId(c.id)}
                                role="tab"
                                aria-selected={questionsTabCategoryId === c.id}
                            >
                                {c.name}
                            </button>
                        ))}
                    </div>
                    {filteredQuestions.length === 0 ? (
                        <div className="triviaMuted">
                            {questionsTabCategoryId === "all" ? "No questions yet." : "No questions in this category yet."}
                        </div>
                    ) : (
                        <div className="triviaTableWrap">
                            <table className="triviaTable">
                                <thead>
                                    <tr>
                                        <th>Category</th>
                                        <th>Question</th>
                                        <th>Answer</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredQuestions.map((q) => {
                                        const cat = categoryById.get(q.categoryId);
                                        return (
                                            <tr key={q.id}>
                                                <td>
                                                    {cat?.name || "(missing category)"}
                                                </td>
                                                <td>
                                                    <textarea
                                                        className="triviaInlineInput triviaTableTextarea"
                                                        value={q.question}
                                                        onChange={(e) => updateQuestion(q.id, { question: e.target.value })}
                                                        rows={2}
                                                    />
                                                </td>
                                                <td>
                                                    <textarea
                                                        className="triviaInlineInput triviaTableTextarea"
                                                        value={q.answer}
                                                        onChange={(e) => updateQuestion(q.id, { answer: e.target.value })}
                                                        rows={2}
                                                    />
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="triviaBtn triviaBtnDanger"
                                                        onClick={() => removeQuestion(q.id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Next steps hint */}
                <div className="triviaFooterHint">
                    Next: we’ll persist this to Firestore (or your backend), then build the actual wheel + play flow.
                </div>
            </div>
        </>
    );
}